import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../lib/api.js';

interface GlobalSettings {
  notifications?: {
    slack?: { enabled: boolean; webhookUrl?: string };
    telegram?: { enabled: boolean; botToken?: string; chatId?: string };
    email?: { enabled: boolean; to?: string };
  };
  messaging?: {
    relayChannel?: 'slack' | 'telegram';
    autoAcknowledge?: boolean;
    slack?: { botToken?: string; channelId?: string; supportChannelId?: string; signingSecret?: string };
    telegram?: { botToken?: string; chatId?: string; supportChatId?: string };
    reviews?: { enabled?: boolean; daysAfterDelivery?: number; maxDaysAfterDelivery?: number };
  };
  killSwitch?: boolean;
}

export function Settings() {
  const [settings, setSettings] = useState<GlobalSettings>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get<GlobalSettings>('/settings').then((s) => {
      setSettings(s);
      setLoading(false);
    });
  }, []);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    await api.put('/settings', settings);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  if (loading) {
    return <div className="h-64 animate-pulse rounded-xl bg-gray-200" />;
  }

  return (
    <form onSubmit={handleSave} className="space-y-6">
      {/* Notifications */}
      <Section title="Notification Channels" description="Configure where alerts and digests are sent.">
        {/* Slack */}
        <ToggleField
          label="Slack"
          enabled={settings.notifications?.slack?.enabled ?? false}
          onToggle={(v) =>
            setSettings((s) => ({
              ...s,
              notifications: { ...s.notifications, slack: { ...s.notifications?.slack, enabled: v } },
            }))
          }
        >
          <Field
            label="Webhook URL"
            value={settings.notifications?.slack?.webhookUrl ?? ''}
            onChange={(v) =>
              setSettings((s) => ({
                ...s,
                notifications: {
                  ...s.notifications,
                  slack: { ...s.notifications?.slack, enabled: true, webhookUrl: v },
                },
              }))
            }
            placeholder="https://hooks.slack.com/services/..."
          />
        </ToggleField>

        {/* Telegram */}
        <ToggleField
          label="Telegram"
          enabled={settings.notifications?.telegram?.enabled ?? false}
          onToggle={(v) =>
            setSettings((s) => ({
              ...s,
              notifications: { ...s.notifications, telegram: { ...s.notifications?.telegram, enabled: v } },
            }))
          }
        >
          <Field
            label="Bot Token"
            value={settings.notifications?.telegram?.botToken ?? ''}
            onChange={(v) =>
              setSettings((s) => ({
                ...s,
                notifications: {
                  ...s.notifications,
                  telegram: { ...s.notifications?.telegram, enabled: true, botToken: v },
                },
              }))
            }
            placeholder="123456:ABC-DEF..."
          />
          <Field
            label="Chat ID"
            value={settings.notifications?.telegram?.chatId ?? ''}
            onChange={(v) =>
              setSettings((s) => ({
                ...s,
                notifications: {
                  ...s.notifications,
                  telegram: { ...s.notifications?.telegram, enabled: true, chatId: v },
                },
              }))
            }
            placeholder="-100123456789"
          />
        </ToggleField>

        {/* Email */}
        <ToggleField
          label="Email"
          enabled={settings.notifications?.email?.enabled ?? false}
          onToggle={(v) =>
            setSettings((s) => ({
              ...s,
              notifications: { ...s.notifications, email: { ...s.notifications?.email, enabled: v } },
            }))
          }
        >
          <Field
            label="To Address"
            value={settings.notifications?.email?.to ?? ''}
            onChange={(v) =>
              setSettings((s) => ({
                ...s,
                notifications: {
                  ...s.notifications,
                  email: { ...s.notifications?.email, enabled: true, to: v },
                },
              }))
            }
            type="email"
            placeholder="you@example.com"
          />
        </ToggleField>
      </Section>

      {/* Messaging */}
      <Section title="Messaging" description="Buyer communication relay and review request settings.">
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600">Relay Channel</label>
            <select
              value={settings.messaging?.relayChannel ?? ''}
              onChange={(v) =>
                setSettings((s) => ({
                  ...s,
                  messaging: { ...s.messaging, relayChannel: v.target.value as 'slack' | 'telegram' || undefined },
                }))
              }
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value="">None (dashboard only)</option>
              <option value="slack">Slack</option>
              <option value="telegram">Telegram</option>
            </select>
          </div>

          {settings.messaging?.relayChannel === 'slack' && (
            <>
              <Field
                label="Slack Bot Token"
                value={settings.messaging?.slack?.botToken ?? ''}
                onChange={(v) =>
                  setSettings((s) => ({
                    ...s,
                    messaging: { ...s.messaging, slack: { ...s.messaging?.slack, botToken: v } },
                  }))
                }
                placeholder="xoxb-..."
              />
              <Field
                label="Alerts Channel ID"
                value={settings.messaging?.slack?.channelId ?? ''}
                onChange={(v) =>
                  setSettings((s) => ({
                    ...s,
                    messaging: { ...s.messaging, slack: { ...s.messaging?.slack, channelId: v } },
                  }))
                }
                placeholder="C0123456789"
              />
              <Field
                label="Support Channel ID"
                value={settings.messaging?.slack?.supportChannelId ?? ''}
                onChange={(v) =>
                  setSettings((s) => ({
                    ...s,
                    messaging: { ...s.messaging, slack: { ...s.messaging?.slack, supportChannelId: v } },
                  }))
                }
                placeholder="C0123456789 (buyer messages go here)"
              />
              <Field
                label="Slack Signing Secret"
                value={settings.messaging?.slack?.signingSecret ?? ''}
                onChange={(v) =>
                  setSettings((s) => ({
                    ...s,
                    messaging: { ...s.messaging, slack: { ...s.messaging?.slack, signingSecret: v } },
                  }))
                }
                placeholder="abc123..."
              />
            </>
          )}

          {settings.messaging?.relayChannel === 'telegram' && (
            <>
              <Field
                label="Telegram Bot Token"
                value={settings.messaging?.telegram?.botToken ?? ''}
                onChange={(v) =>
                  setSettings((s) => ({
                    ...s,
                    messaging: { ...s.messaging, telegram: { ...s.messaging?.telegram, botToken: v } },
                  }))
                }
                placeholder="123456:ABC-DEF..."
              />
              <Field
                label="Alerts Chat ID"
                value={settings.messaging?.telegram?.chatId ?? ''}
                onChange={(v) =>
                  setSettings((s) => ({
                    ...s,
                    messaging: { ...s.messaging, telegram: { ...s.messaging?.telegram, chatId: v } },
                  }))
                }
                placeholder="-100123456789"
              />
              <Field
                label="Support Chat ID"
                value={settings.messaging?.telegram?.supportChatId ?? ''}
                onChange={(v) =>
                  setSettings((s) => ({
                    ...s,
                    messaging: { ...s.messaging, telegram: { ...s.messaging?.telegram, supportChatId: v } },
                  }))
                }
                placeholder="-100123456789 (buyer messages go here)"
              />
            </>
          )}

          <ToggleField
            label="Auto-acknowledge"
            enabled={settings.messaging?.autoAcknowledge ?? false}
            onToggle={(v) =>
              setSettings((s) => ({
                ...s,
                messaging: { ...s.messaging, autoAcknowledge: v },
              }))
            }
          >
            <p className="text-xs text-gray-500">Automatically send acknowledgment for simple message categories (shipping inquiries, feedback).</p>
          </ToggleField>

          <ToggleField
            label="Review Requests"
            enabled={settings.messaging?.reviews?.enabled ?? false}
            onToggle={(v) =>
              setSettings((s) => ({
                ...s,
                messaging: { ...s.messaging, reviews: { ...s.messaging?.reviews, enabled: v } },
              }))
            }
          >
            <div className="flex gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600">Min Days After Delivery</label>
                <input
                  type="number"
                  min={4}
                  max={30}
                  value={settings.messaging?.reviews?.daysAfterDelivery ?? 5}
                  onChange={(e) =>
                    setSettings((s) => ({
                      ...s,
                      messaging: {
                        ...s.messaging,
                        reviews: { ...s.messaging?.reviews, enabled: true, daysAfterDelivery: parseInt(e.target.value) },
                      },
                    }))
                  }
                  className="mt-1 block w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Max Days After Delivery</label>
                <input
                  type="number"
                  min={5}
                  max={30}
                  value={settings.messaging?.reviews?.maxDaysAfterDelivery ?? 30}
                  onChange={(e) =>
                    setSettings((s) => ({
                      ...s,
                      messaging: {
                        ...s.messaging,
                        reviews: { ...s.messaging?.reviews, enabled: true, maxDaysAfterDelivery: parseInt(e.target.value) },
                      },
                    }))
                  }
                  className="mt-1 block w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Amazon allows solicitations between 5-30 days after delivery.</p>
          </ToggleField>
        </div>
      </Section>

      {/* AI Connections — MCP Servers */}
      <Section title="AI Connections (MCP)" description="Connect AI assistants like Claude, ChatGPT, or Gemini to your Amazon data.">
        <div className="space-y-4">
          {/* Sellvana MCP */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
              <span className="text-sm font-semibold text-blue-900">Sellvana MCP (Intelligence Layer)</span>
            </div>
            <p className="text-xs text-blue-700 mb-3">
              Your data, analytics, automation rules, COGS, inventory, competitive intel — all from D1 with unlimited history.
            </p>
            <div className="bg-white rounded border border-blue-100 p-3">
              <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">MCP Server URL</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-gray-50 px-2 py-1.5 rounded border font-mono text-gray-800 select-all">
                  https://sellvana-mcp.vandreus.workers.dev/mcp
                </code>
                <button type="button" onClick={() => navigator.clipboard.writeText('https://sellvana-mcp.vandreus.workers.dev/mcp')}
                  className="px-2 py-1.5 text-xs bg-gray-100 rounded hover:bg-gray-200 text-gray-600">Copy</button>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">OAuth — enter dashboard password when prompted</p>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {['Sales & Orders', 'PPC Automation', 'COGS & Margins', 'Inventory', 'Keywords', 'Competitive Intel', 'Communication'].map(t => (
                <span key={t} className="px-1.5 py-0.5 text-[10px] bg-blue-100 text-blue-700 rounded">{t}</span>
              ))}
            </div>
          </div>

          {/* Amazon Ads MCP */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-sm font-semibold text-amber-900">Amazon Ads MCP (Execution Layer)</span>
              <span className="text-[10px] px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded">Optional</span>
            </div>
            <p className="text-xs text-amber-700 mb-3">
              Amazon's official MCP server (open beta). Create campaigns, manage bids, run reports, access DSP & AMC via natural language. 50+ tools.
            </p>
            <div className="bg-white rounded border border-amber-100 p-3">
              <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">How to Connect</label>
              <ol className="text-xs text-gray-700 space-y-1.5 list-decimal list-inside">
                <li>Go to <a href="https://advertising.amazon.com/API/docs/en-us/mcp/mcp-overview" target="_blank" rel="noopener" className="text-blue-600 underline">Amazon Ads MCP docs</a></li>
                <li>Use your existing Amazon Ads API credentials (same as Sellvana uses)</li>
                <li>In Claude Desktop or ChatGPT: add a new MCP connection pointing to Amazon's server</li>
                <li>Both Sellvana + Amazon Ads MCP work side by side — Claude routes automatically</li>
              </ol>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {['Campaign CRUD', 'Bid Management', 'Ad-hoc Reports', 'Cross-Market Expand', 'DSP', 'AMC Queries', 'Billing'].map(t => (
                <span key={t} className="px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-800 rounded">{t}</span>
              ))}
            </div>
          </div>

          {/* How they work together */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <p className="text-xs font-semibold text-gray-700 mb-2">How they work together</p>
            <div className="text-xs text-gray-600 space-y-1">
              <p><strong>"How are my campaigns doing?"</strong> → Sellvana (instant from D1, with COGS/margin context)</p>
              <p><strong>"Create a campaign for ASIN X in UK"</strong> → Amazon Ads MCP (orchestrated multi-step workflow)</p>
              <p><strong>"What keywords should I negate?"</strong> → Sellvana (search term analysis + wasted spend)</p>
              <p><strong>"What's my new-to-brand rate?"</strong> → Amazon Ads MCP (AMC clean room query)</p>
            </div>
          </div>
        </div>
      </Section>

      {/* BSA Compliance + Emergency Stop moved to System Health page */}

      {/* Save */}
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-brand-600 px-6 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        {saved && <span className="text-sm text-green-600">Saved!</span>}
      </div>
    </form>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <h3 className="text-sm font-semibold">{title}</h3>
      <p className="mt-1 text-xs text-gray-500">{description}</p>
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}

function ToggleField({
  label,
  enabled,
  onToggle,
  children,
}: {
  label: string;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gray-100 p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <button
          type="button"
          onClick={() => onToggle(!enabled)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            enabled ? 'bg-brand-600' : 'bg-gray-200'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      {enabled && <div className="mt-3 space-y-3">{children}</div>}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        placeholder={placeholder}
      />
    </div>
  );
}


